import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import "./valueChain.css";
import ValueChainMarkup from "./ValueChainMarkup";
import { CHANNEL_TIPS, DEFAULT_PRODUCT, DEFAULT_VALUES, INPUT_IDS, buildCsv, compute } from "../../lib/valueChainMath";

const PRODUCT_FIELDS = [
  ["name", "SKU / Item Name", "text", { placeholder: "SKU / Item name" }],
  ["price", "Invoice Price", "number", { placeholder: "0.00", step: "0.01", min: "0" }, "$"],
  ["cogs", "COGS / Unit", "number", { placeholder: "0.00", step: "0.01", min: "0" }, "$"],
  ["royalty", "Royalty %", "number", { placeholder: "0", step: "0.5", min: "0", max: "100" }, null, "%"],
  ["units", "Units / Year", "number", { placeholder: "0", min: "0" }],
];

function ProductRow({ p, onField, onRemove, ro }) {
  return (
    <tr id={`prod-${p.id}`} data-testid={`vc-product-row-${p.id}`}>
      {PRODUCT_FIELDS.map(([key, label, type, attrs, prefix, suffix]) => {
        const input = <input type={type} value={p[key]} disabled={ro} onChange={(e) => onField(p.id, key, e.target.value)} {...attrs} />;
        return (
          <td key={key} data-label={label}>
            {prefix ? <div className="input-prefix"><span>{prefix}</span>{input}</div>
              : suffix ? <div className="input-suffix"><span>{suffix}</span>{input}</div> : input}
          </td>
        );
      })}
      <td data-label=""><button className="remove-btn" onClick={() => onRemove(p.id)} title="Remove" data-testid={`vc-remove-${p.id}`}>×</button></td>
    </tr>
  );
}

const withIds = (rows, start) => rows.map((r, i) => ({ id: start + i + 1, ...r }));

// Mirror how the browser sanitised values when the iframe set input.value:
// range → clamped/stepped (empty = midpoint 50); number → "" if not numeric.
const TEXT_IDS = new Set(["customerName", "startDate"]);
function sanitize(k, raw) {
  const s = raw == null ? "" : String(raw);
  if (TEXT_IDS.has(k)) return s;
  if (k === "probability") {
    const n = parseFloat(s);
    return String(isNaN(n) ? 50 : Math.min(100, Math.max(0, Math.round(n / 5) * 5)));
  }
  return /^-?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/.test(s) ? s : "";
}

// Native Value Chain Calculator — same cpg* bridge + `value-chain-1`
// snapshot shape as the former iframe so saved Projects keep working.
const ValueChainTool = forwardRef(function ValueChainTool({ onSave }, ref) {
  const [v, setV] = useState(DEFAULT_VALUES);
  const [products, setProducts] = useState(() => withIds([DEFAULT_PRODUCT], 0));
  const [channel, setChannel] = useState("retail");
  const [ro, setRo] = useState(false);
  const nextId = useRef(1);
  const latest = useRef({});
  latest.current = { v, products, channel };

  const o = useMemo(() => compute(v, products, channel), [v, products, channel]);
  const on = (e) => { const { id, value } = e.target; setV((s) => ({ ...s, [id]: value })); };
  const addProduct = (r = { name: "", price: "", cogs: "", royalty: "", units: "" }) => {
    const id = ++nextId.current;
    setProducts((ps) => [...ps, { id, ...r }]);
  };
  const onField = (id, key, value) => setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, [key]: value } : p)));
  const onRemove = (id) => setProducts((ps) => ps.filter((p) => p.id !== id));

  useImperativeHandle(ref, () => ({
    cpgCollectState: () => {
      const { v: cv, products: cp, channel: cc } = latest.current;
      return {
        __schema: "value-chain-1", channel: cc, ...cv,
        products: cp.map(({ name, price, cogs, royalty, units }) => ({ name, price, cogs, royalty, units })),
      };
    },
    cpgApplyState: (snap) => {
      if (!snap || typeof snap !== "object") return;
      const next = { ...DEFAULT_VALUES, ...latest.current.v };
      INPUT_IDS.forEach((k) => { if (k in snap) next[k] = sanitize(k, snap[k]); });
      const rows = Array.isArray(snap.products) && snap.products.length
        ? snap.products.map((r) => ({ name: r.name || "", price: sanitize("price", r.price || ""), cogs: sanitize("cogs", r.cogs || ""), royalty: sanitize("royalty", r.royalty || ""), units: sanitize("units", r.units || "") }))
        : [DEFAULT_PRODUCT];
      setV(next);
      setProducts(withIds(rows, 0));
      nextId.current = rows.length;
      setChannel(CHANNEL_TIPS[snap.channel] ? snap.channel : "retail");
    },
    cpgSetViewOnly: (flag) => setRo(!!flag),
  }), []);

  const exportCsv = () => {
    const { csv, filename } = buildCsv(v, products, channel, o);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = filename;
    a.click();
  };
  const fire = (verb) => { if (!ro) onSave?.(verb); };

  const rows = products.map((p) => <ProductRow key={p.id} p={p} onField={onField} onRemove={onRemove} ro={ro} />);
  return (
    <div className={`vc-native cpg-tool${ro ? " cpg-view-only" : ""}`} data-testid="value-chain-native">
      <ValueChainMarkup v={v} on={on} o={o} channel={channel} setChannel={setChannel} tips={CHANNEL_TIPS[channel]}
        productRows={rows} addProduct={() => addProduct()} exportCsv={exportCsv} fire={fire} ro={ro} />
    </div>
  );
});

export default ValueChainTool;
